import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Link,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from 'shared/hooks/usePageTitle';
import { passwordRecoveryApi } from 'shared/services/passwordRecoveryApi';

export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  usePageTitle('Recuperar Contraseña', 'Solicitar enlace de recuperación de contraseña');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Por favor ingrese su email');
      return;
    }

    if (!validateEmail(email)) {
      setError('Por favor ingrese un email válido');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const data = await passwordRecoveryApi.forgotPassword(email);

      if (data.success) {
        setMessage(data.message || 'Se ha enviado un enlace de recuperación a su email');
        setEmailSent(true);

        // Opcional: redirigir al login después de unos segundos
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Revise su email para el enlace de recuperación de contraseña'
            }
          });
        }, 5000);
      } else {
        setError(data.error || 'Error al procesar la solicitud');
      }
    } catch (err: unknown) {
      setError('Error de conexión. Por favor intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ 
        minHeight: '100dvh', 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        py: 3
      }}>
        <Card sx={{ 
          width: '100%',
          textAlign: 'center',
          p: 2,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: 3
        }}>
          {/* Logo de marca de agua */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 20,
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: 'none'
            }}
          >
            <img 
              src="/safety-logo.png" 
              alt="" 
              style={{ 
                width: '80px',
                height: '80px',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          <CardContent sx={{ position: 'relative', zIndex: 1, p: { xs: 2.5, sm: 4 } }}>
            {!emailSent ? (
              <>
                {/* Ícono de email */}
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: (theme) => theme.palette.background.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px auto'
                }}>
                  <EmailIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>

                <Typography variant="h4" gutterBottom sx={{ color: (theme) => theme.palette.background.primary, mb: 2 }}>
                  Recuperar Contraseña
                </Typography>

                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                  Ingrese su email y le enviaremos un enlace seguro para restablecer su contraseña
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Correo Electrónico"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!error && !message}
                    placeholder="correo@ejemplo.com"
                    required
                    disabled={loading}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: (theme) => theme.palette.background.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: (theme) => theme.palette.background.primary,
                      },
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }
                    }}
                  />

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    sx={{
                      py: 1.5,
                      mb: 3,
                      bgcolor: (theme) => theme.palette.background.buttonBg,
                      color: (theme) => theme.palette.background.buttonText,
                      '&:hover': {
                        bgcolor: (theme) => theme.palette.background.buttonBg,
                        filter: 'brightness(0.9)',
                      }
                    }}
                  >
                    {loading ? 'Enviando...' : 'Enviar Enlace de Recuperación'}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                {/* Estado de email enviado */}
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px auto'
                }}>
                  <SendIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>

                <Typography variant="h4" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
                  ¡Email Enviado!
                </Typography>

                {message && (
                  <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                    {message}
                  </Alert>
                )}

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Si el email <strong>{email}</strong> está registrado en nuestro sistema,
                  recibirá un enlace de recuperación en los próximos minutos.
                </Typography>

                <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
                  Revise su bandeja de entrada y spam. El enlace expirará en 1 hora por seguridad.
                </Typography>
              </>
            )}

            {/* Botón para volver al login */}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToLogin}
              disabled={loading}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 200 },
                borderColor: (theme) => theme.palette.background.primary,
                color: (theme) => theme.palette.background.primary,
                '&:hover': {
                  borderColor: (theme) => theme.palette.background.primary,
                  bgcolor: (theme) => `${theme.palette.background.primary}08`,
                }
              }}
            >
              Volver al Inicio de Sesión
            </Button>

            {!emailSent && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  ¿Recordó su contraseña?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={handleBackToLogin}
                    underline="hover"
                    disabled={loading}
                    sx={{ color: (theme) => theme.palette.background.primary }}
                  >
                    Iniciar sesión
                  </Link>
                </Typography>
              </Box>
            )}

            {emailSent && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  ¿No recibiste el email?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      setEmailSent(false);
                      setMessage('');
                      setError('');
                    }}
                    underline="hover"
                    sx={{ color: (theme) => theme.palette.background.primary }}
                  >
                    Intentar de nuevo
                  </Link>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};